import { useState, useEffect } from 'react'
import { C, font } from '../design'
import { getChunkSettings, saveChunkSettings, getChunksStats } from '../utils/api'
import { DEFAULT_CHUNK_SETTINGS, saveSettingsLocally, groupRemainder } from '../utils/chunkSettings'

const TYPE_LABELS = {
  journal:  { label: 'Личный дневник',    hint: 'записи дневника' },
  personal: { label: 'Личные чаты 1:1',  hint: 'переписки 1:1' },
  resume:   { label: 'Резюме',            hint: 'профессиональный контекст' },
  threads:  { label: 'Threads посты',     hint: 'публичные посты' },
  group:    { label: 'Групповые чаты',    hint: 'остаток (авто)' },
}

const MODES = ['min', '=', 'max']
const MODE_LABELS = { min: 'min', '=': '=', max: 'max' }

function StatChip({ label, messages, chunks }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
      <span style={{ color: C.textMuted, fontSize: 11 }}>{label}:</span>
      {messages != null && (
        <span style={{ fontFamily: font.mono, fontSize: 11, color: 'rgba(180,185,212,0.7)' }}>
          {typeof messages === 'string' ? messages : `~${messages.toLocaleString('ru')}`}
        </span>
      )}
      {chunks != null && chunks > 0 && (
        <span style={{ fontFamily: font.mono, fontSize: 10, color: 'rgba(180,185,212,0.4)' }}>
          · {chunks} чанк{chunks === 1 ? '' : chunks < 5 ? 'а' : 'ов'}
        </span>
      )}
    </div>
  )
}

export default function ChunkSettingsPanel({ userId: ownerUserId = 'user' }) {
  const [participants, setParticipants] = useState([])
  const [selectedId, setSelectedId] = useState('default')
  const [settings, setSettings] = useState(DEFAULT_CHUNK_SETTINGS)
  const [stats, setStats] = useState(null)
  const [saving, setSaving] = useState(false)
  const [saved, setSaved] = useState(false)
  const [loading, setLoading] = useState(false)

  // Fetch participants list once
  useEffect(() => {
    fetch(`${import.meta.env.VITE_API_URL || 'http://92.53.107.15'}/api/participants`)
      .then(r => r.json())
      .then(data => setParticipants(Array.isArray(data) ? data : []))
      .catch(() => {})
  }, [])

  // On entity change: fetch settings + stats
  useEffect(() => {
    const uid = resolveUserId(selectedId)
    setLoading(true)
    Promise.all([
      getChunkSettings(uid),
      getChunksStats(uid),
    ]).then(([settingsRes, statsRes]) => {
      if (settingsRes?.settings) {
        setSettings({ ...DEFAULT_CHUNK_SETTINGS, ...settingsRes.settings, quotas: { ...DEFAULT_CHUNK_SETTINGS.quotas, ...settingsRes.settings.quotas } })
        saveSettingsLocally(uid, settingsRes.settings)
      } else {
        setSettings(DEFAULT_CHUNK_SETTINGS)
      }
      if (!statsRes?.error) setStats(statsRes)
      else setStats(null)
    }).catch(() => {
      setSettings(DEFAULT_CHUNK_SETTINGS)
      setStats(null)
    }).finally(() => setLoading(false))
  }, [selectedId])

  function resolveUserId(id) {
    if (id === 'default') return 'default'
    if (id === 'owner') return ownerUserId
    return `participant_${id}`
  }

  function resolveLabel(id) {
    if (id === 'default') return 'По умолчанию'
    if (id === 'owner') return 'Владелец (я)'
    const p = participants.find(p => String(p.id) === String(id))
    return p ? p.display_name : id
  }

  async function handleSave() {
    setSaving(true)
    setSaved(false)
    const uid = resolveUserId(selectedId)
    await saveChunkSettings(uid, settings)
    saveSettingsLocally(uid, settings)
    setSaving(false)
    setSaved(true)
    setTimeout(() => setSaved(false), 2500)
  }

  function updateQuota(type, field, value) {
    setSettings(s => ({
      ...s,
      quotas: { ...s.quotas, [type]: { ...s.quotas[type], [field]: value } }
    }))
    setSaved(false)
  }

  const remainder = groupRemainder(settings)
  const t = stats?.by_type || {}

  const statsByType = {
    journal:  { chunks: t.journal || 0 },
    personal: { chunks: t.personal || 0 },
    resume:   { chunks: t.resume || 0 },
    threads:  { chunks: (t.threads || 0) + (t.threads_post || 0) },
    group:    { chunks: t.group || 0 },
  }

  const panelStyle = {
    marginBottom: 24, padding: '18px 20px',
    background: 'rgba(184,189,212,0.04)',
    border: '1px solid rgba(184,189,212,0.12)',
    borderRadius: 10,
  }

  return (
    <div style={panelStyle}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ fontFamily: font.mono, fontSize: 11, color: C.accent, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
          Настройки выборки чанков
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          style={{
            padding: '4px 14px', borderRadius: 4, cursor: saving ? 'wait' : 'pointer',
            fontFamily: font.body, fontSize: 11,
            background: saved ? 'rgba(77,200,138,0.15)' : 'rgba(184,189,212,0.08)',
            border: `1px solid ${saved ? 'rgba(77,200,138,0.4)' : 'rgba(184,189,212,0.2)'}`,
            color: saved ? C.green : C.silverLo,
          }}
        >
          {saving ? 'Сохраняю...' : saved ? '✓ Сохранено' : 'Сохранить'}
        </button>
      </div>

      {/* Entity selector */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 6 }}>Настройка для:</div>
        <select
          value={selectedId}
          onChange={e => setSelectedId(e.target.value)}
          style={{
            width: '100%', padding: '7px 10px', borderRadius: 6,
            background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(184,189,212,0.15)',
            color: C.text, fontFamily: font.body, fontSize: 13, cursor: 'pointer',
          }}
        >
          <option value="default">По умолчанию (для всех без персональной настройки)</option>
          <option value="owner">Владелец (я)</option>
          {participants.map(p => (
            <option key={p.id} value={String(p.id)}>{p.display_name}</option>
          ))}
        </select>
      </div>

      {loading && (
        <div style={{ fontSize: 12, color: C.textMuted, textAlign: 'center', padding: '12px 0' }}>Загрузка...</div>
      )}

      {!loading && (
        <>
          {/* Stats + stat chips */}
          {stats && stats.total > 0 && (
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: '4px 20px',
              padding: '8px 12px', marginBottom: 16,
              background: 'rgba(255,255,255,0.02)', borderRadius: 6,
              border: '1px solid rgba(184,189,212,0.07)',
            }}>
              {t.group > 0 && <StatChip label="Групп. чаты" messages={`~${((t.group || 0) * 40).toLocaleString('ru')} сообщ.`} chunks={t.group} />}
              {t.personal > 0 && <StatChip label="Личные чаты" messages={`~${((t.personal || 0) * 40).toLocaleString('ru')} сообщ.`} chunks={t.personal} />}
              {t.journal > 0 && <StatChip label="Дневник" messages={`${t.journal} зап.`} chunks={t.journal} />}
              {(t.threads || t.threads_post) > 0 && <StatChip label="Threads" messages={`${(t.threads || 0) + (t.threads_post || 0)} постов`} chunks={(t.threads || 0) + (t.threads_post || 0)} />}
              {t.resume > 0 && <StatChip label="Резюме" messages="✓" chunks={t.resume} />}
              <span style={{ marginLeft: 'auto', fontFamily: font.mono, fontSize: 10, color: 'rgba(184,189,212,0.3)' }}>
                {stats.total} чанков всего
              </span>
            </div>
          )}
          {stats && stats.total === 0 && (
            <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 16 }}>Нет данных в базе для {resolveLabel(selectedId)}</div>
          )}

          {/* Total N */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 18, paddingBottom: 14, borderBottom: '1px solid rgba(184,189,212,0.08)' }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, color: C.text, fontWeight: 500 }}>Всего чанков для анализа (N)</div>
              <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2 }}>Общий бюджет выборки</div>
            </div>
            <input
              type="number" min="10" max="200" step="5"
              value={settings.n}
              onChange={e => { setSettings(s => ({ ...s, n: Math.max(10, parseInt(e.target.value) || 40) })); setSaved(false) }}
              style={numInputStyle}
            />
          </div>

          {/* Per-type quotas */}
          <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 10 }}>Квоты по типу источника:</div>
          {['journal', 'personal', 'resume', 'threads'].map(ct => {
            const q = settings.quotas?.[ct] || { mode: 'min', value: 0 }
            const avail = statsByType[ct]?.chunks ?? null
            const overQuota = avail !== null && q.value > avail && avail > 0
            return (
              <div key={ct} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                {/* Label */}
                <div style={{ width: 160, flexShrink: 0 }}>
                  <div style={{ fontSize: 13, color: C.text }}>{TYPE_LABELS[ct].label}</div>
                  <div style={{ fontSize: 10, color: C.textMuted }}>
                    {TYPE_LABELS[ct].hint}
                    {avail !== null && <span style={{ color: 'rgba(184,189,212,0.4)' }}> · {avail} чанков</span>}
                  </div>
                </div>
                {/* Mode selector */}
                <div style={{ display: 'flex', borderRadius: 4, overflow: 'hidden', border: '1px solid rgba(184,189,212,0.15)', flexShrink: 0 }}>
                  {MODES.map(m => (
                    <button
                      key={m}
                      onClick={() => updateQuota(ct, 'mode', m)}
                      style={{
                        padding: '4px 8px', border: 'none', cursor: 'pointer',
                        fontFamily: font.mono, fontSize: 11,
                        background: q.mode === m ? 'rgba(184,189,212,0.18)' : 'rgba(255,255,255,0.02)',
                        color: q.mode === m ? C.silverHi : C.silverLo,
                        transition: 'all 0.15s',
                      }}
                    >
                      {MODE_LABELS[m]}
                    </button>
                  ))}
                </div>
                {/* Value */}
                <input
                  type="number" min="0" max="50" step="1"
                  value={q.value}
                  onChange={e => updateQuota(ct, 'value', Math.max(0, parseInt(e.target.value) || 0))}
                  style={{ ...numInputStyle, borderColor: overQuota ? 'rgba(224,152,88,0.4)' : 'rgba(184,189,212,0.15)' }}
                />
                {overQuota && (
                  <div style={{ fontSize: 10, color: C.orange }}>доступно {avail}</div>
                )}
              </div>
            )
          })}

          {/* Group remainder */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 4, paddingTop: 12, borderTop: '1px solid rgba(184,189,212,0.08)' }}>
            <div style={{ width: 160, flexShrink: 0 }}>
              <div style={{ fontSize: 13, color: C.textMuted }}>{TYPE_LABELS.group.label}</div>
              <div style={{ fontSize: 10, color: 'rgba(184,189,212,0.3)' }}>
                {TYPE_LABELS.group.hint}
                {statsByType.group.chunks > 0 && <span> · {statsByType.group.chunks} чанков</span>}
              </div>
            </div>
            <div style={{ ...numInputStyle, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textMuted, pointerEvents: 'none', userSelect: 'none' }}>
              {remainder}
            </div>
            <div style={{ fontSize: 11, color: 'rgba(184,189,212,0.35)' }}>авто (N − сумма)</div>
          </div>

          {/* Fast mode toggle */}
          <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px solid rgba(184,189,212,0.08)' }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={settings.fast_mode || false}
                onChange={e => { setSettings(s => ({ ...s, fast_mode: e.target.checked })); setSaved(false) }}
                style={{ marginTop: 2, accentColor: C.accent, flexShrink: 0 }}
              />
              <div>
                <div style={{ fontSize: 13, color: C.text }}>Оптимизированный быстрый анализ</div>
                <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2, lineHeight: 1.5 }}>
                  Пропускает генерацию доказательств (confidence%, паттерны, цитаты). Экономит 20–30% токенов. Вкладка "Доказательства" будет пустой.
                </div>
              </div>
            </label>
          </div>
        </>
      )}
    </div>
  )
}

const numInputStyle = {
  width: 64, padding: '5px 8px', borderRadius: 4, textAlign: 'center',
  background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(184,189,212,0.15)',
  color: C.silverHi, fontFamily: font.mono, fontSize: 13,
}
